import * as THREE from 'three'
import {
  makeGridTexture,
  makeSkyTexture,
  makeStripeTexture,
  resolveTexture as resolveTextureFromDef,
} from './textures'
import type {
  Fog,
  Light,
  MapDescriptor,
  MapDescriptorV1,
  MapDescriptorV2,
  Material,
  Mesh,
  Sky,
  Texture as TextureDef,
  Trigger,
  V1BoxBrush,
  V1Light,
  V1Material,
  Vec3,
} from './mapdescriptor'
import { isV1, isV2 } from './mapdescriptor'

// What the renderer + collision system needs to start the game.
// Preserved 1:1 from the legacy world.ts so consumers (main.ts, collision.ts) don't change.
export interface WorldData {
  scene: THREE.Scene
  collisionMeshes: THREE.Mesh[]
  spawnPosition: THREE.Vector3
  endTriggerCenter: THREE.Vector3
  endTriggerHalfSize: THREE.Vector3
}

// Dispatch on `version`. Throws on unknown versions (caller should catch).
export function buildWorldFromDescriptor(desc: MapDescriptor): WorldData {
  if (isV2(desc)) return buildV2(desc)
  if (isV1(desc)) return buildV1(desc)
  throw new Error(`Unsupported map descriptor version: ${(desc as { version: unknown }).version}`)
}

// ─── v1 (legacy) ─────────────────────────────────────────────────────────────

function buildV1(desc: MapDescriptorV1): WorldData {
  const scene = new THREE.Scene()
  applySky(scene, desc.sky)
  applyFog(scene, desc.fog)
  applyV1Lights(scene, desc.lights)

  const materials = new Map<string, THREE.Material>()
  for (const [name, m] of Object.entries(desc.materials)) {
    materials.set(name, makeV1Material(m))
  }

  const collisionMeshes: THREE.Mesh[] = []
  for (const brush of desc.brushes) {
    const mesh = buildV1Brush(brush, materials)
    scene.add(mesh)
    collisionMeshes.push(mesh)
    addEdges(mesh, scene)
  }

  const endTrigger = desc.endTrigger
  return {
    scene,
    collisionMeshes,
    spawnPosition: new THREE.Vector3(desc.spawn[0], desc.spawn[1], desc.spawn[2]),
    endTriggerCenter: new THREE.Vector3(endTrigger.center[0], endTrigger.center[1], endTrigger.center[2]),
    endTriggerHalfSize: new THREE.Vector3(endTrigger.halfSize[0], endTrigger.halfSize[1], endTrigger.halfSize[2]),
  }
}

function buildV1Brush(brush: V1BoxBrush, materials: Map<string, THREE.Material>): THREE.Mesh {
  const mat = materials.get(brush.material)
  if (!mat) throw new Error(`v1 brush references unknown material: ${brush.material}`)
  const [sx, sy, sz] = brush.size
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), mat)
  mesh.position.set(brush.position[0], brush.position[1], brush.position[2])
  mesh.rotation.set(brush.rotation[0], brush.rotation[1], brush.rotation[2])
  return mesh
}

function makeV1Material(m: V1Material): THREE.MeshLambertMaterial {
  let tex: THREE.CanvasTexture
  if (m.type === 'grid') {
    tex = makeGridTexture(m.base, m.line, m.cells)
  } else {
    tex = makeStripeTexture(m.base, m.stripe, m.bands)
  }
  tex.repeat.set(m.repeat[0], m.repeat[1])
  return new THREE.MeshLambertMaterial({ map: tex })
}

function applyV1Lights(scene: THREE.Scene, lights: V1Light[]): void {
  for (const l of lights) {
    const color = new THREE.Color(l.color)
    if (l.type === 'ambient') {
      scene.add(new THREE.AmbientLight(color, l.intensity))
    } else if (l.type === 'directional') {
      const d = new THREE.DirectionalLight(color, l.intensity)
      if (l.position) d.position.set(l.position[0], l.position[1], l.position[2])
      scene.add(d)
    } else if (l.type === 'point') {
      const p = new THREE.PointLight(color, l.intensity)
      if (l.position) p.position.set(l.position[0], l.position[1], l.position[2])
      scene.add(p)
    }
  }
}

// ─── v2 ──────────────────────────────────────────────────────────────────────

function buildV2(desc: MapDescriptorV2): WorldData {
  const scene = new THREE.Scene()
  applySky(scene, desc.sky)
  applyFog(scene, desc.fog)
  applyV2Lights(scene, desc.lights)

  // Resolve materials. Each material gets a (cached) Three.js Material.
  const materials = new Map<string, THREE.Material>()
  for (const [id, mat] of Object.entries(desc.materials)) {
    materials.set(id, makeV2Material(mat, desc.textures))
  }

  const collisionMeshes: THREE.Mesh[] = []
  for (const m of desc.meshes) {
    const three = buildV2Mesh(m, materials)
    scene.add(three)
    if (m.collidable !== false) collisionMeshes.push(three)
    addEdges(three, scene)
  }

  // Pick spawn: first 'T' or 'any'. Fallback to first.
  const spawn = pickSpawn(desc)
  // Pick a timer_end trigger as the run-finish AABB (preserves legacy contract).
  const end = pickEndTrigger(desc.triggers)

  return {
    scene,
    collisionMeshes,
    spawnPosition: new THREE.Vector3(spawn[0], spawn[1], spawn[2]),
    endTriggerCenter: new THREE.Vector3(end.center[0], end.center[1], end.center[2]),
    endTriggerHalfSize: new THREE.Vector3(end.halfSize[0], end.halfSize[1], end.halfSize[2]),
  }
}

function pickSpawn(desc: MapDescriptorV2): Vec3 {
  const tSpawn = desc.spawns.find((s) => s.team === 'T')
  if (tSpawn) return tSpawn.position
  const any = desc.spawns.find((s) => s.team === 'any')
  if (any) return any.position
  if (desc.spawns.length > 0) return desc.spawns[0]!.position
  return [0, 64, 256]
}

function pickEndTrigger(triggers: Trigger[]): { center: Vec3; halfSize: Vec3 } {
  const end = triggers.find((t) => t.type === 'timer_end')
  if (end) return { center: end.center, halfSize: end.halfSize }
  return { center: [0, -1e6, 0], halfSize: [1, 1, 1] }
}

function buildV2Mesh(m: Mesh, materials: Map<string, THREE.Material>): THREE.Mesh {
  const mat = materials.get(m.materialId)
  if (!mat) throw new Error(`v2 mesh '${m.id}' references unknown material: ${m.materialId}`)

  const geom = new THREE.BufferGeometry()
  geom.setAttribute('position', new THREE.Float32BufferAttribute(m.vertices, 3))
  if (m.normals.length > 0) {
    geom.setAttribute('normal', new THREE.Float32BufferAttribute(m.normals, 3))
  }
  if (m.uvs.length > 0) {
    geom.setAttribute('uv', new THREE.Float32BufferAttribute(m.uvs, 2))
  }
  if (m.indices.length > 0) {
    geom.setIndex(m.indices)
  }
  if (m.normals.length === 0) geom.computeVertexNormals()

  const mesh = new THREE.Mesh(geom, mat)
  mesh.castShadow = m.castShadow ?? false
  if (m.origin) mesh.position.set(m.origin[0], m.origin[1], m.origin[2])
  return mesh
}

function resolveMaterialTexture(textureId: string | null, textures: Record<string, TextureDef>): THREE.Texture | null {
  if (!textureId) return null
  const def = textures[textureId]
  if (!def) return null
  return resolveTextureFromDef(def)
}

function makeV2Material(mat: Material, textures: Record<string, TextureDef>): THREE.Material {
  if (mat.shader === 'Water') {
    return new THREE.MeshStandardMaterial({
      color: 0x3a78a0,
      transparent: true,
      opacity: 0.5,
    })
  }

  const tex = resolveMaterialTexture(mat.textureId, textures)
  if (tex) {
    tex.repeat.set(mat.textureScale[0], mat.textureScale[1])
    tex.offset.set(mat.textureOffset[0], mat.textureOffset[1])
  }

  // UnlitGeneric → Basic. Everything else uses Lambert (cheaper than Standard,
  // matches the legacy procedural map's lighting).
  if (mat.shader === 'UnlitGeneric') {
    return new THREE.MeshBasicMaterial({
      map: tex,
      transparent: mat.translucent,
      alphaTest: mat.alphaTest,
    })
  }

  return new THREE.MeshLambertMaterial({
    map: tex,
    transparent: mat.translucent,
    alphaTest: mat.alphaTest,
  })
}

function applyV2Lights(scene: THREE.Scene, lights: Light[]): void {
  for (const l of lights) {
    const color = new THREE.Color(l.color)
    if (l.type === 'ambient') {
      scene.add(new THREE.AmbientLight(color, l.intensity))
    } else if (l.type === 'directional') {
      const d = new THREE.DirectionalLight(color, l.intensity)
      // Three.js DirectionalLight aims from .position toward .target (default = origin).
      // Source v2 `direction` is the *travel* direction of light, so the light source
      // is in the opposite direction. `position` is the legacy/Three.js-native form
      // and is used directly.
      if (l.direction) {
        d.position.set(-l.direction[0], -l.direction[1], -l.direction[2])
      } else if (l.position) {
        d.position.set(l.position[0], l.position[1], l.position[2])
      }
      scene.add(d)
    } else if (l.type === 'point') {
      const p = new THREE.PointLight(color, l.intensity, l.range)
      p.position.set(l.position[0], l.position[1], l.position[2])
      scene.add(p)
    }
  }
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function applySky(scene: THREE.Scene, sky: Sky): void {
  if (sky.type === 'procedural-starfield') {
    scene.background = makeSkyTexture()
    return
  }
  // image-cubemap: load the six face URLs. For native maps this branch is unused;
  // for BSP imports the texture loader will resolve via the descriptor's `textures`
  // map. Left intentionally simple — proper cubemap loading lives in a later pass.
  scene.background = makeSkyTexture()
}

function applyFog(scene: THREE.Scene, fog: Fog | null): void {
  if (!fog) return
  scene.fog = new THREE.Fog(new THREE.Color(fog.color), fog.near, fog.far)
}

function addEdges(mesh: THREE.Mesh, scene: THREE.Scene): void {
  const edges = new THREE.EdgesGeometry(mesh.geometry, 1)
  const line = new THREE.LineSegments(
    edges,
    new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.45, fog: false }),
  )
  line.position.copy(mesh.position)
  line.rotation.copy(mesh.rotation)
  scene.add(line)
}
